// Chrome will not reliably print a PDF-viewer iframe that is hidden, so the PDF
// is rasterized into an HTML page and printed from a hidden same-tab frame.
const RENDER_SCALE = 2
const THUMBNAIL_SCALE = 0.35

let pdfjsPromise: Promise<typeof import('react-pdf').pdfjs> | undefined

function loadPdfjs(): Promise<typeof import('react-pdf').pdfjs> {
  pdfjsPromise ??= import('react-pdf').then(({ pdfjs }) => {
    pdfjs.GlobalWorkerOptions.workerSrc = new URL(
      'pdfjs-dist/build/pdf.worker.min.mjs',
      import.meta.url
    ).toString()
    return pdfjs
  })
  return pdfjsPromise
}

export function preloadPdfRenderer(): void {
  void loadPdfjs()
}

async function renderPdfPages(blob: Blob): Promise<string[]> {
  const pdfjs = await loadPdfjs()
  const pdf = await pdfjs.getDocument({ data: await blob.arrayBuffer() })
    .promise
  try {
    const pages: string[] = []
    for (let pageNumber = 1; pageNumber <= pdf.numPages; pageNumber++) {
      const page = await pdf.getPage(pageNumber)
      const viewport = page.getViewport({ scale: RENDER_SCALE })
      const canvas = document.createElement('canvas')
      canvas.width = viewport.width
      canvas.height = viewport.height
      await page.render({ canvas, viewport }).promise
      pages.push(canvas.toDataURL('image/png'))
    }
    return pages
  } finally {
    await pdf.destroy()
  }
}

export function printPageImages(pages: string[], title: string): void {
  const iframe = document.createElement('iframe')
  iframe.setAttribute('aria-hidden', 'true')
  iframe.title = title
  iframe.style.display = 'none'
  document.body.appendChild(iframe)

  const frameWindow = iframe.contentWindow
  const frameDocument = iframe.contentDocument
  if (!frameWindow || !frameDocument) {
    iframe.remove()
    return
  }

  frameDocument.title = title
  const style = frameDocument.createElement('style')
  style.textContent = `
    @page { margin: 0; }
    html, body { margin: 0; padding: 0; }
    img { display: block; width: 100%; page-break-after: always; break-after: page; }
    img:last-child { page-break-after: auto; break-after: auto; }
  `
  frameDocument.head.appendChild(style)

  const images = pages.map((src) => {
    const img = document.createElement('img')
    img.src = src
    img.alt = ''
    return img
  })
  frameDocument.body.replaceChildren(...images)

  frameWindow.addEventListener('afterprint', () => iframe.remove(), {
    once: true,
  })

  void Promise.all(images.map((img) => img.decode().catch(() => {}))).then(
    () => {
      frameWindow.focus()
      frameWindow.print()
    }
  )
}

export async function printPdfBlob(blob: Blob, title: string): Promise<void> {
  printPageImages(await renderPdfPages(blob), title)
}

// Fax pending documents are blob URLs. Orbit's CSP allows blob: in img-src but
// not in frame-src, so the scaled PDF-viewer iframe thumbnail stays blank on
// preview/production until the full-size modal (also fixed via frame-src).
export async function renderPdfBlobPreviewDataUrl(blob: Blob): Promise<string> {
  const pdfjs = await loadPdfjs()
  const pdf = await pdfjs.getDocument({ data: await blob.arrayBuffer() })
    .promise
  try {
    const page = await pdf.getPage(1)
    const viewport = page.getViewport({ scale: THUMBNAIL_SCALE })
    const canvas = document.createElement('canvas')
    canvas.width = viewport.width
    canvas.height = viewport.height
    await page.render({ canvas, viewport }).promise
    return canvas.toDataURL('image/png')
  } finally {
    await pdf.destroy()
  }
}
